import { useEffect, useMemo, useRef, useState } from 'react';
import { BeakerIcon, CalendarIcon, GlobeAltIcon, HeartIcon } from '@heroicons/react/outline';
import TopBar from '../components/TopBar';
import TableExportMenu from '../components/shared/TableExportMenu';
import { useUser } from '../context/UserContext';
import authService from '../services/authService';
import { fetchPowerBISemanticData } from '../services/powerbiService';
import { OPENAI_PROXY_BASE_URL } from '../components/api';
import '../modules/webv2/webV2Module.css';

const hpvImplementationSchedule = [
  {
    key: 'new-launches',
    label: 'New launches',
    cells: [
      { lines: ['Venezuela,', 'India ph1&2', 'Burundi'] },
      { lines: ['Vietnam'], italic: true },
      { lines: ['Pakistan ph2'] },
      { lines: ['Chad, DRC ph1,', 'Guinea, Guinea-Bissau, Haiti,', 'Lebanon, Philippines'], italic: true }
    ]
  },
  {
    key: 'delayed-macs',
    label: 'Delayed MACs',
    cells: [
      { lines: ['Cambodia (TBC)'] },
      { lines: ['Burkina Faso'] },
      { lines: ['Cameroon,', 'Myanmar'] },
      { lines: ['Cameroon'] }
    ]
  },
  {
    key: 'coverage-improvement',
    label: 'Coverage Improvement',
    secondLineLabel: '& Integrated Delivery',
    cells: [
      { lines: ['Pakistan', 'Djibouti', 'Tunisia', 'Angola', 'Bangladesh'] },
      { lines: ['Tanzania', 'Kenya', 'Mauritania', 'Morocco'], italic: true },
      { lines: ['Gambia,', 'Eritrea,', 'Sierra Leone,', 'Zimbabwe,', 'Eswatini'], italic: true },
      { lines: ['Lesotho,', 'Togo,', 'Senegal,', 'Sri Lanka,', 'Guyana, Armenia'], italic: true }
    ]
  }
];

const HPV_ANTIGEN_COLUMN = 'HPV';

const hpvIntroductionCategoryMeta = {
  'new-launches': {
    label: 'HPV new introduction',
    matrixClass: 'is-hpv-new-introduction',
    priority: 1
  },
  'delayed-macs': {
    label: 'HPV delayed MAC',
    matrixClass: 'is-hpv-follow-up',
    priority: 2
  },
  'coverage-improvement': {
    label: 'HPV coverage improvement',
    matrixClass: 'is-hpv-follow-up',
    priority: 2
  }
};

const normalizePlannedCountryName = (value) => {
  const normalized = String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\([^)]*\)/g, '')
    .replace(/\bph(?:ase)?\s*\d+(?:\s*&\s*\d+)?\b/gi, '')
    .replace(/\btbc\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

  const aliases = {
    'drc': 'democratic republic of the congo',
    'democratic republic of congo': 'democratic republic of the congo',
    'congo, democratic republic of the': 'democratic republic of the congo',
    'the gambia': 'gambia'
  };

  return aliases[normalized] || normalized;
};

const getRowField = (row, ...names) => {
  if (!row) return undefined;

  const entries = Object.entries(row);
  for (const name of names) {
    if (row[name] !== undefined && row[name] !== null && row[name] !== '') return row[name];
    const match = entries.find(([key]) => key.toLowerCase() === String(name).toLowerCase());
    if (match && match[1] !== undefined && match[1] !== null && match[1] !== '') return match[1];
  }

  return undefined;
};

const parseVeNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value > 0 && value <= 1 ? value * 100 : value;
  }

  const numericValue = Number(String(value).replace('%', '').replace(',', '.').trim());
  if (!Number.isFinite(numericValue)) return null;
  return numericValue > 0 && numericValue <= 1 ? numericValue * 100 : numericValue;
};

const compactAntigenKey = (value) => String(value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');

const WHO_VACCINE_PROFILES = [
  // MEASLES
  {
    keys: ['MCV1', 'MCV', 'MEASLES'],
    veMin: 84,
    veMax: 93,
    whoUrl: 'https://www.who.int/publications/i/item/who-wer9217-205-227'
  },
  {
    keys: ['MCV2'],
    veMin: 94,
    veMax: 99,
    whoUrl: 'https://www.who.int/publications/i/item/who-wer9217-205-227'
  },

  // MR / MMR — measles component when used as measles indicator
  {
    keys: ['MR', 'MMR'],
    veMin: 92,
    veMax: 99,
    whoUrl: 'https://www.who.int/publications/i/item/who-wer9217-205-227'
  },

  // POLIO
  {
    keys: ['IPV1'],
    veMin: 80,
    veMax: 100,
    whoUrl: 'https://www.who.int/publications/i/item/WHO-WER9725-277-300'
  },
  {
    keys: ['IPV2', 'IPV'],
    veMin: 90,
    veMax: 100,
    whoUrl: 'https://www.who.int/publications/i/item/WHO-WER9725-277-300'
  },
  {
    keys: ['BOPV', 'OPV', 'POL3', 'POLIO'],
    veMin: 80,
    veMax: 95,
    whoUrl: 'https://www.who.int/publications/i/item/WHO-WER9725-277-300'
  },

  // HEPATITIS B
  {
    keys: ['HEPB3', 'HEPB'],
    veMin: 95,
    veMax: 100,
    whoUrl: 'https://www.who.int/publications/i/item/WER9227/'
  },

  // HepB birth dose should NOT be treated as equivalent to HepB3
  {
    keys: ['HEPBBD'],
    veMin: 70,
    veMax: 95,
    whoUrl: 'https://www.who.int/publications/i/item/WER9227/'
  },

  // Hib
  {
    keys: ['HIB3', 'HIB'],
    veMin: 94,
    veMax: 99,
    whoUrl: 'https://www.who.int/publications/i/item/who-wer8839-413-426'
  },

  // PNEUMOCOCCAL CONJUGATE VACCINE
  {
    keys: ['PCV3', 'PCV10', 'PCV13', 'PCV'],
    veMin: 80,
    veMax: 95,
    whoUrl: 'https://www.who.int/publications/i/item/who-wer10039-411-437'
  },

  // ROTAVIRUS
  // Large setting-dependent variation, particularly by mortality stratum.
  {
    keys: ['ROTAC', 'ROTA', 'ROTAVIRUS'],
    veMin: 50,
    veMax: 95,
    whoUrl: 'https://www.who.int/publications/i/item/WHO-WER9628'
  },

  // DTP / PENTAVALENT
  // This value represents protection against pertussis.
  {
    keys: ['DTP3', 'DTP', 'PENTA', 'PENTAVALENT', 'DTPPENTA', 'DTPHEPBHIB'],
    veMin: 80,
    veMax: 90,
    whoUrl: 'https://www.who.int/publications/i/item/WHO-WER9035'
  },

  // BCG
  // Protection against severe childhood TB, NOT pulmonary TB in adults.
  {
    keys: ['BCG'],
    veMin: 70,
    veMax: 90,
    whoUrl: 'https://www.who.int/publications/i/item/who-wer9308-73-96'
  },

  // HPV
  // Protection against vaccine-type HPV infection / high-grade lesions.
  {
    keys: ['HPV', 'HPV1', 'HPV2'],
    veMin: 90,
    veMax: 100,
    whoUrl: 'https://www.who.int/publications/i/item/who-wer9750-645-672'
  },

  // YELLOW FEVER
  {
    keys: ['YF', 'YELLOWFEVER'],
    veMin: 90,
    veMax: 99,
    whoUrl: 'https://www.who.int/publications-detail-redirect/who-wer8827'
  },

  // JAPANESE ENCEPHALITIS
  {
    keys: ['JE', 'JAPANESEENCEPHALITIS'],
    veMin: 80,
    veMax: 99,
    whoUrl: 'https://www.who.int/publications-detail-redirect/japanese-encephalitis-vaccines-who-position-paper'
  },

  // MENINGOCOCCAL
  // Product/serogroup dependent.
  {
    keys: ['MENA', 'MENACWY', 'MENINGOCOCCAL'],
    veMin: 80,
    veMax: 95,
    whoUrl: 'https://www.who.int/publications-detail-redirect/meningococcal-vaccines-who-position-paper-november-2011'
  },

  // TETANUS-CONTAINING VACCINES
  {
    keys: ['TD', 'TT', 'TDAP'],
    veMin: 95,
    veMax: 100,
    whoUrl: 'https://www.who.int/publications/i/item/WHO-WER9206'
  },

  // TYPHOID CONJUGATE VACCINE
  // Updated WHO position paper August 2026.
  {
    keys: ['TCV', 'TYPHOID'],
    veMin: 79,
    veMax: 88,
    whoUrl: 'https://www.who.int/publications/i/item/who-wer10132-166-180'
  },

  // RUBELLA
  {
    keys: ['RUBELLA', 'RCV', 'RCV1'],
    veMin: 95,
    veMax: 100,
    whoUrl: 'https://www.who.int/teams/immunization-vaccines-and-biologicals/policies/position-papers/rubella'
  }
];

const findWhoVaccineProfile = (...names) => {
  const candidates = names.map(compactAntigenKey).filter(Boolean);

  for (const candidate of candidates) {
    const exact = WHO_VACCINE_PROFILES.find((profile) => profile.keys.includes(candidate));
    if (exact) return exact;
  }

  for (const candidate of candidates) {
    const partial = WHO_VACCINE_PROFILES.find((profile) => (
      profile.keys.some((key) => candidate.includes(key) || key.includes(candidate))
    ));
    if (partial) return partial;
  }

  return null;
};

const formatPercent = (value) => {
  if (value === null || value === undefined || value === '') return 'N/A';
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? `${numericValue.toFixed(0)}%` : 'N/A';
};

const formatPercentRange = (minValue, maxValue) => {
  const min = parseVeNumber(minValue);
  const max = parseVeNumber(maxValue);
  if (!Number.isFinite(min) && !Number.isFinite(max)) return 'N/A';
  if (Number.isFinite(min) && Number.isFinite(max)) return `${min.toFixed(0)}-${max.toFixed(0)}%`;
  return `${(Number.isFinite(min) ? min : max).toFixed(0)}%`;
};

const getLatestCoverageValue = (series) => {
  const records = Array.isArray(series) ? [...series] : [];
  const latest = records.reverse().find((item) => Number.isFinite(Number(item?.coverage)));
  return latest ? Number(latest.coverage) : null;
};

const formatEffectivePopulationProtection = (antigen) => {
  const coverage = getLatestCoverageValue(antigen?.wuenicSeries);
  const minVe = parseVeNumber(antigen?.typicalVeMin);
  const maxVe = parseVeNumber(antigen?.typicalVeMax);

  if (!Number.isFinite(coverage) || (!Number.isFinite(minVe) && !Number.isFinite(maxVe))) return 'N/A';

  const minProtection = Number.isFinite(minVe) ? (coverage * minVe) / 100 : null;
  const maxProtection = Number.isFinite(maxVe) ? (coverage * maxVe) / 100 : minProtection;

  return formatPercentRange(minProtection, maxProtection);
};

const formatIntroDate = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const rawValue = String(value).trim();
  const dateMatch = rawValue.match(/^(\d{4}-\d{2}-\d{2})/);

  return dateMatch?.[1] || rawValue;
};

const formatScheduleTimeline = (value) => {
  const rawValue = String(value || '').trim();
  if (!rawValue) return '';

  const yearMatch = rawValue.match(/^(\d{4})\b[\s:,-]*/);
  const scheduleWithoutYear = yearMatch ? rawValue.slice(yearMatch[0].length).trim() : rawValue;

  return scheduleWithoutYear
    .replace(/\bweeks?\b/gi, 'w')
    .replace(/\bwks?\b/gi, 'w')
    .replace(/\bmonths?\b/gi, 'm')
    .replace(/\bmos?\b/gi, 'm')
    .replace(/\b(\d+)\s+([wm])\b/gi, '$1$2')
    .replace(/\bbirth\b/gi, 'Birth')
    .replace(/\bB\b/g, 'Birth')
    .replace(/\s*[-,;|/]+\s*/g, ' → ')
    .replace(/\s*→\s*/g, ' → ')
    .replace(/\s+/g, ' ')
    .trim();
};

const normalizeColumn = (name) => {
  const raw = String(name || '').trim();
  const bracketMatch = raw.match(/\[([^\]]+)\]\s*$/);
  return String(bracketMatch ? bracketMatch[1] : raw).trim().toLowerCase();
};

const findColumnKey = (columns, names) => columns.find((col) => names.includes(normalizeColumn(col))) || null;
const getRowText = (row, columnKey) => (columnKey ? String(row?.[columnKey] || '').trim() : '');
const VACCINE_PROGRAMMES_TA_TEAM_NAME = 'Immunization - NVIDR';
const getTaRequestKey = (row, index, titleColumnKey) => `${getRowText(row, titleColumnKey) || 'nvidr-ta-request'}-${index}`;
const isVaccineProgrammesTaTeam = (value) => String(value || '').trim().toLowerCase().replace(/\s+/g, ' ').includes(VACCINE_PROGRAMMES_TA_TEAM_NAME.toLowerCase());
const taRowMatchesSelectedCountry = (row, matchValues, taCountryColumnKey, taIso3ColumnKey) => {
  if (!matchValues.size) return false;

  const candidates = [
    getRowText(row, taCountryColumnKey),
    getRowText(row, taIso3ColumnKey)
  ]
    .map((value) => String(value || '').trim().toLowerCase())
    .filter(Boolean);

  return candidates.some((value) => matchValues.has(value));
};

const formatDocumentDate = (value) => {
  if (!value) return 'N/A';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleDateString();
};

const buildDocumentUrl = (row, nameColumnKey, folderColumnKey) => {
  const name = getRowText(row, nameColumnKey);
  const folder = getRowText(row, folderColumnKey);
  if (!name || !folder) return '';

  const base = folder.endsWith('/') ? folder : `${folder}/`;
  return encodeURI(`${base}${name}`);
};

const buildHpvIntroductionByCountry = () => {
  const quarters = ['Q1 2026', 'Q2 2026', 'Q3 2026', 'Q4 2026'];
  const countries = new Map();

  hpvImplementationSchedule.forEach((category) => {
    const meta = hpvIntroductionCategoryMeta[category.key];
    if (!meta) return;

    category.cells.forEach((cell, quarterIndex) => {
      (cell?.lines || [])
        .flatMap((line) => String(line || '').split(','))
        .map((country) => country.trim())
        .filter(Boolean)
        .forEach((country) => {
          const key = normalizePlannedCountryName(country);
          if (!key) return;

          const current = countries.get(key);
          const quarter = quarters[quarterIndex];

          if (current && current.priority <= meta.priority) {
            if (quarter && !current.quarters.includes(quarter)) current.quarters.push(quarter);
            return;
          }

          countries.set(key, {
            ...meta,
            categoryLabel: category.label,
            quarters: quarter ? [quarter] : []
          });
        });
    });
  });

  return countries;
};

export const getHpvIntroductionPlanCount = (countryNames) => {
  const countries = buildHpvIntroductionByCountry();
  if (!Array.isArray(countryNames)) return countries.size;

  const wanted = new Set(countryNames.map((name) => normalizePlannedCountryName(name)).filter(Boolean));
  let count = 0;
  countries.forEach((_, key) => {
    if (wanted.has(key)) count += 1;
  });
  return count;
};

const NewVaccineIntroContent = () => {
  const { user } = useUser();
  const [epiScheduleRows, setEpiScheduleRows] = useState([]);
  const [isEpiScheduleLoading, setIsEpiScheduleLoading] = useState(false);
  const [countryFilter, setCountryFilter] = useState('gavi');
  const [selectedCountryKey, setSelectedCountryKey] = useState('');
  const [showAllMatrixCountries, setShowAllMatrixCountries] = useState(false);
  const [aiNarratives, setAiNarratives] = useState({});
  const [isNarrativeLoading, setIsNarrativeLoading] = useState(false);
  const [narrativeError, setNarrativeError] = useState('');
  const [immunizationDocRows, setImmunizationDocRows] = useState([]);
  const [immunizationDocColumns, setImmunizationDocColumns] = useState([]);
  const [isImmunizationDocsLoading, setIsImmunizationDocsLoading] = useState(true);
  const [immunizationDocsError, setImmunizationDocsError] = useState('');
  const [taRows, setTaRows] = useState([]);
  const [taColumns, setTaColumns] = useState([]);
  const [isTaLoading, setIsTaLoading] = useState(true);
  const [taError, setTaError] = useState('');
  const [expandedTaRequestKeys, setExpandedTaRequestKeys] = useState({});

  const apiBase = process.env.REACT_APP_API_BASE_URL || 'https://uni-func-pd-thrive360.azurewebsites.net/api';
  const immunizationDocsDatasetId = String(process.env.REACT_APP_COUNTRY_IMMUNIZATION_DOCUMENTS_DATASET_ID || '0ed08684-eb4d-4256-8f39-70dbaedb2633').trim() || undefined; //remeber its a semnatic model id not report id 
  const immunizationDocsWorkspaceId = process.env.REACT_APP_COUNTRY_IMMUNIZATION_DOCUMENTS_WORKSPACE_ID
    || process.env.REACT_APP_COE_TA_TRACKER_WORKSPACE_ID
    || '7d441575-4aa9-4260-99dd-543f002c0340';
  const immunizationDocsDaxQuery = process.env.REACT_APP_COUNTRY_IMMUNIZATION_DOCUMENTS_DAX_QUERY || '';
  const immunizationDocsTableName = process.env.REACT_APP_COUNTRY_IMMUNIZATION_DOCUMENTS_TABLE_NAME || 'DocumentsDb';
  const taDatasetId = process.env.REACT_APP_COE_TA_TRACKER_DATASET_ID || '1ad5473d-bb83-4e5b-9980-7424a841f9b6';
  const taWorkspaceId = process.env.REACT_APP_COE_TA_TRACKER_WORKSPACE_ID || '7d441575-4aa9-4260-99dd-543f002c0340';
  const taDaxQuery = process.env.REACT_APP_COE_TA_TRACKER_DAX_QUERY || '';
  const taTableName = process.env.REACT_APP_COE_TA_TRACKER_TABLE_NAME || 'TARequests';
  const normalizedAccessLevel = String(user?.accessLevel || '').toLowerCase();

  const allowedIso3Values = useMemo(() => {
    const fromAllowed = Array.isArray(user?.allowedCountriesIso3) ? user.allowedCountriesIso3 : [];
    const fromCountry = user?.country_iso3 ? [user.country_iso3] : [];
    return [...fromAllowed, ...fromCountry]
      .map((value) => String(value || '').trim().toUpperCase())
      .filter(Boolean)
      .filter((value, idx, arr) => arr.indexOf(value) === idx);
  }, [user?.allowedCountriesIso3, user?.country_iso3]);

  useEffect(() => {
    let mounted = true;

    const loadEpiSchedule = async () => {
      setIsEpiScheduleLoading(true);
      try {
        const idToken = await authService.getIdToken();
        const isGlobalScope = normalizedAccessLevel === 'global' || normalizedAccessLevel === 'admin';
        const filters = ["LOWER(TRIM(COALESCE(e.`Type`, ''))) LIKE '%vaccin%'"];

        if (!isGlobalScope && allowedIso3Values.length > 0) {
          filters.push(`e.iso3 IN (${allowedIso3Values.map((iso3) => `'${iso3.replace(/'/g, "''")}'`).join(', ')})`);
        }

        const query = `
          SELECT
            e.iso3,
            g.Country_Name,
            e.\`Type\`,
            e.Antigen_Name,
            e.\`Schedule\`,
            e.Intro_Date,
            e.RI_Antigen,
            e.Gavi_Funded,
            wy.vaccine AS Wuenic_Vaccine,
            wy.max_year AS Wuenic_Latest_Year,
            wy.max_year - 4 AS Wuenic_Year_1,
            MAX(CASE WHEN w.year = wy.max_year - 4 THEN w.wuenic END) AS Wuenic_Coverage_1,
            wy.max_year - 3 AS Wuenic_Year_2,
            MAX(CASE WHEN w.year = wy.max_year - 3 THEN w.wuenic END) AS Wuenic_Coverage_2,
            wy.max_year - 2 AS Wuenic_Year_3,
            MAX(CASE WHEN w.year = wy.max_year - 2 THEN w.wuenic END) AS Wuenic_Coverage_3,
            wy.max_year - 1 AS Wuenic_Year_4,
            MAX(CASE WHEN w.year = wy.max_year - 1 THEN w.wuenic END) AS Wuenic_Coverage_4,
            wy.max_year AS Wuenic_Year_5,
            MAX(CASE WHEN w.year = wy.max_year THEN w.wuenic END) AS Wuenic_Coverage_5
          FROM thrive_appdb_prd.epi_schedule e
          LEFT JOIN thrive_appdb_prd.geo_admin g ON e.iso3 = g.iso3
          LEFT JOIN (
            SELECT iso3, vaccine, MAX(year) AS max_year
            FROM thrive_appdb_prd.wuenic_data_coverage
            GROUP BY iso3, vaccine
          ) wy
            ON wy.iso3 = e.iso3
            AND wy.vaccine = CASE
              WHEN UPPER(TRIM(e.Antigen_Name)) = 'BCG' THEN 'BCG'
              WHEN UPPER(TRIM(e.Antigen_Name)) IN ('DTP', 'DTP3', 'DTP_PENTA', 'PENTA', 'PENTAVALENT') THEN 'DTP3'
              WHEN UPPER(TRIM(e.Antigen_Name)) IN ('HEPB', 'HEPB3') THEN 'HepB3'
              WHEN UPPER(TRIM(e.Antigen_Name)) IN ('HIB', 'HIB3') THEN 'Hib3'
              WHEN UPPER(TRIM(e.Antigen_Name)) IN ('IPV', 'IPV1') THEN 'IPV1'
              WHEN UPPER(TRIM(e.Antigen_Name)) = 'IPV2' THEN 'IPV2'
              WHEN UPPER(TRIM(e.Antigen_Name)) IN ('MCV', 'MCV1', 'MEASLES', 'MR') THEN 'MCV1'
              WHEN UPPER(TRIM(e.Antigen_Name)) = 'MCV2' THEN 'MCV2'
              WHEN UPPER(TRIM(e.Antigen_Name)) IN ('PCV', 'PCV3') THEN 'PCV3'
              WHEN UPPER(TRIM(e.Antigen_Name)) IN ('BOPV', 'OPV', 'POLIO', 'POL3') THEN 'Pol3'
              WHEN UPPER(TRIM(e.Antigen_Name)) IN ('ROTA', 'ROTAC', 'ROTAVIRUS') THEN 'RotaC'
              ELSE NULL
            END
          LEFT JOIN thrive_appdb_prd.wuenic_data_coverage w
            ON w.iso3 = wy.iso3
            AND w.vaccine = wy.vaccine
            AND w.year BETWEEN wy.max_year - 4 AND wy.max_year
          WHERE ${filters.join(' AND ')}
          GROUP BY
            e.iso3,
            g.Country_Name,
            e.\`Type\`,
            e.Antigen_Name,
            e.\`Schedule\`,
            e.Intro_Date,
            e.RI_Antigen,
            e.Gavi_Funded,
            wy.vaccine,
            wy.max_year
          ORDER BY g.Country_Name, e.\`Type\`, e.Antigen_Name;
        `;

        const response = await fetch(`${apiBase}/db-query`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`
          },
          body: JSON.stringify({ query }),
          mode: 'cors'
        });

        if (!response.ok) {
          throw new Error(`EPI schedule request failed (${response.status})`);
        }

        const payload = await response.json();
        const resultRows = Array.isArray(payload?.result) ? payload.result : (Array.isArray(payload) ? payload : []);

        if (!mounted) return;
        setEpiScheduleRows(resultRows);
      } catch (error) {
        if (!mounted) return;
        setEpiScheduleRows([]);
      } finally {
        if (mounted) setIsEpiScheduleLoading(false);
      }
    };

    loadEpiSchedule();

    return () => {
      mounted = false;
    };
  }, [apiBase, normalizedAccessLevel, allowedIso3Values]);

  useEffect(() => {
    let mounted = true;

    const loadImmunizationDocuments = async () => {
      setIsImmunizationDocsLoading(true);
      setImmunizationDocsError('');

      try {
        if (!immunizationDocsDatasetId) {
          throw new Error('Missing REACT_APP_COUNTRY_IMMUNIZATION_DOCUMENTS_DATASET_ID.');
        }

        const effectiveQuery = String(immunizationDocsDaxQuery || '').trim() || undefined;
        const effectiveTableName = effectiveQuery ? undefined : (String(immunizationDocsTableName || '').trim() || undefined);
        const data = await fetchPowerBISemanticData(
          immunizationDocsDatasetId,
          immunizationDocsWorkspaceId,
          effectiveQuery,
          5000,
          effectiveTableName
        );

        if (!mounted) return;
        setImmunizationDocRows(Array.isArray(data?.data) ? data.data : []);
        setImmunizationDocColumns(Array.isArray(data?.columns) ? data.columns : []);
      } catch (error) {
        if (!mounted) return;
        setImmunizationDocRows([]);
        setImmunizationDocColumns([]);
        setImmunizationDocsError(error?.message || 'Country immunization documents are unavailable right now.');
      } finally {
        if (mounted) setIsImmunizationDocsLoading(false);
      }
    };

    loadImmunizationDocuments();

    return () => {
      mounted = false;
    };
  }, [
    immunizationDocsDatasetId,
    immunizationDocsWorkspaceId,
    immunizationDocsDaxQuery,
    immunizationDocsTableName,
    normalizedAccessLevel,
    allowedIso3Values
  ]);

  useEffect(() => {
    let mounted = true;

    const loadTaRequests = async () => {
      setIsTaLoading(true);
      setTaError('');

      try {
        const effectiveQuery = String(taDaxQuery || '').trim() || undefined;
        const effectiveTableName = effectiveQuery ? undefined : (String(taTableName || '').trim() || undefined);
        const data = await fetchPowerBISemanticData(
          taDatasetId,
          taWorkspaceId,
          effectiveQuery,
          5000,
          effectiveTableName
        );

        if (!mounted) return;
        setTaRows(Array.isArray(data?.data) ? data.data : []);
        setTaColumns(Array.isArray(data?.columns) ? data.columns : []);
      } catch (loadError) {
        if (!mounted) return;
        setTaRows([]);
        setTaColumns([]);
        setTaError('TA Hub requests are unavailable right now.');
      } finally {
        if (mounted) setIsTaLoading(false);
      }
    };

    loadTaRequests();

    return () => {
      mounted = false;
    };
  }, [taDatasetId, taWorkspaceId, taDaxQuery, taTableName]);

  const immunizationDocNameColumnKey = useMemo(
    () => findColumnKey(immunizationDocColumns, ['name', 'document name', 'file name', 'title']),
    [immunizationDocColumns]
  );
  const immunizationDocIso3ColumnKey = useMemo(
    () => findColumnKey(immunizationDocColumns, ['iso3', 'country iso3', 'country_iso3', 'country code']),
    [immunizationDocColumns]
  );
  const immunizationDocFolderColumnKey = useMemo(
    () => findColumnKey(immunizationDocColumns, ['folder path', 'folder', 'path', 'url', 'link']),
    [immunizationDocColumns]
  );
  const immunizationDocModifiedColumnKey = useMemo(
    () => findColumnKey(immunizationDocColumns, ['date modified', 'modified', 'last modified']),
    [immunizationDocColumns]
  );

  const epiScheduleSummary = useMemo(() => {
    const rows = Array.isArray(epiScheduleRows) ? epiScheduleRows : [];
    const countries = new Set();
    const antigens = new Set();
    let routineAntigens = 0;
    let gaviFunded = 0;

    rows.forEach((row) => {
      const iso3 = String(row?.iso3 || '').trim().toUpperCase();
      const antigen = String(row?.Antigen_Name || '').trim();
      const isRoutine = String(row?.RI_Antigen || '').trim().toLowerCase() === 'yes';
      const isGaviFunded = String(row?.Gavi_Funded || '').trim().toLowerCase() === 'yes';

      if (iso3) countries.add(iso3);
      if (antigen) antigens.add(antigen);
      if (isRoutine) routineAntigens += 1;
      if (isGaviFunded) gaviFunded += 1;
    });

    return [
      { key: 'countries', label: 'Countries in scope', value: countries.size },
      { key: 'antigens', label: 'Unique antigens', value: antigens.size },
      { key: 'routine', label: 'RI schedule items', value: routineAntigens },
      { key: 'gavi', label: 'Gavi funded items', value: gaviFunded }
    ];
  }, [epiScheduleRows]);

  const countryProfiles = useMemo(() => {
    const countries = new Map();

    epiScheduleRows.forEach((row) => {
      const iso3 = String(row?.iso3 || '').trim().toUpperCase();
      const countryName = String(row?.Country_Name || iso3 || 'Unknown country').trim();
      const key = iso3 || countryName;
      const antigen = String(getRowField(row, 'Antigen_Name', 'Antigen') || '').trim();
      const schedule = String(row?.Schedule || '').trim();
      const scheduleTimeline = formatScheduleTimeline(schedule);
      const introDate = formatIntroDate(row?.Intro_Date);
      const isRoutine = String(row?.RI_Antigen || '').trim().toLowerCase() === 'yes';
      const isGaviFunded = String(row?.Gavi_Funded || '').trim().toLowerCase() === 'yes';

      if (!countries.has(key)) {
        countries.set(key, {
          key,
          iso3,
          countryName,
          antigens: [],
          routineCount: 0,
          gaviFundedCount: 0,
          notRoutineCount: 0,
          isGaviCountry: false
        });
      }

      const profile = countries.get(key);
      if (isGaviFunded) profile.isGaviCountry = true;
      if (isRoutine) profile.routineCount += 1;
      if (isGaviFunded) profile.gaviFundedCount += 1;
      if (!isRoutine) profile.notRoutineCount += 1;
      if (antigen) {
        const whoProfile = findWhoVaccineProfile(antigen, row?.Wuenic_Vaccine);
        profile.antigens.push({
          name: antigen,
          scheduleTimeline,
          introDate,
          isRoutine,
          isGaviFunded,
          wuenicVaccine: row?.Wuenic_Vaccine || '',
          protectionBreadth: whoProfile?.diseases || '',
          typicalVeMin: whoProfile?.veMin ?? null,
          typicalVeMax: whoProfile?.veMax ?? null,
          publicHealthImpact: '',
          whoUrl: whoProfile?.whoUrl || '',
          wuenicSeries: [
            { year: row?.Wuenic_Year_1, coverage: row?.Wuenic_Coverage_1 },
            { year: row?.Wuenic_Year_2, coverage: row?.Wuenic_Coverage_2 },
            { year: row?.Wuenic_Year_3, coverage: row?.Wuenic_Coverage_3 },
            { year: row?.Wuenic_Year_4, coverage: row?.Wuenic_Coverage_4 },
            { year: row?.Wuenic_Year_5, coverage: row?.Wuenic_Coverage_5 }
          ].filter((item) => item.year !== null && item.year !== undefined && item.year !== '')
        });
      }
    });

    return Array.from(countries.values()).sort((a, b) => a.countryName.localeCompare(b.countryName));
  }, [epiScheduleRows]);

  const antigenColumns = useMemo(() => {
    const antigenCounts = new Map();

    epiScheduleRows.forEach((row) => {
      const antigen = String(row?.Antigen_Name || '').trim();
      if (!antigen) return;
      antigenCounts.set(antigen, (antigenCounts.get(antigen) || 0) + 1);
    });

    return Array.from(antigenCounts.entries())
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .slice(0, 14)
      .map(([antigen]) => antigen);
  }, [epiScheduleRows]);

  const matrixAntigenColumns = useMemo(() => {
    return antigenColumns;
  }, [antigenColumns]);

  const filteredCountries = useMemo(() => {
    return countryProfiles.filter((profile) => {
      if (countryFilter === 'gavi') return profile.isGaviCountry;
      return !profile.isGaviCountry;
    });
  }, [countryProfiles, countryFilter]);

  const selectedCountry = useMemo(() => {
    return filteredCountries.find((profile) => profile.key === selectedCountryKey) || filteredCountries[0] || null;
  }, [filteredCountries, selectedCountryKey]);

  const selectedCountryImmunizationDocuments = useMemo(() => {
    const selectedIso3 = String(selectedCountry?.iso3 || '').trim().toUpperCase();
    if (!selectedIso3) return [];

    return immunizationDocRows
      .filter((row) => (
        getRowText(row, immunizationDocIso3ColumnKey).toUpperCase() === selectedIso3
      ))
      .sort((a, b) => {
        const left = new Date(getRowText(a, immunizationDocModifiedColumnKey)).getTime();
        const right = new Date(getRowText(b, immunizationDocModifiedColumnKey)).getTime();
        return (Number.isFinite(right) ? right : 0) - (Number.isFinite(left) ? left : 0);
      });
  }, [
    selectedCountry?.iso3,
    immunizationDocRows,
    immunizationDocIso3ColumnKey,
    immunizationDocModifiedColumnKey
  ]);

  useEffect(() => {
    if (!selectedCountry) return;
    setSelectedCountryKey(selectedCountry.key);
  }, [selectedCountry]);

  const taLeadCoeTeamColumnKey = useMemo(
    () => findColumnKey(taColumns, ['lead coe team (accountable)', 'lead coe team', 'lead coe', 'coe team']),
    [taColumns]
  );
  const taCountryColumnKey = useMemo(
    () => findColumnKey(taColumns, ['country']),
    [taColumns]
  );
  const taIso3ColumnKey = useMemo(
    () => findColumnKey(taColumns, ['iso3', 'country iso3', 'country_iso3', 'country code', 'country iso']),
    [taColumns]
  );
  const taTitleColumnKey = useMemo(
    () => findColumnKey(taColumns, [
      'request title',
      'request',
      'title',
      'activity',
      'intervention',
      'action',
      'issue'
    ]),
    [taColumns]
  );
  const taDescriptionColumnKey = useMemo(
    () => findColumnKey(taColumns, ['description', 'request description']),
    [taColumns]
  );
  const taStatusColumnKey = useMemo(
    () => findColumnKey(taColumns, ['implementation status', 'status']),
    [taColumns]
  );
  const taLeadStaffColumnKey = useMemo(
    () => findColumnKey(taColumns, ['ta lead staff', 'ta lead staff (accountable)']),
    [taColumns]
  );
  const selectedCountryMatchValues = useMemo(() => {
    const values = new Set();
    if (!selectedCountry) return values;
    const countryName = String(selectedCountry.countryName || '').trim().toLowerCase();
    const iso3 = String(selectedCountry.iso3 || '').trim().toLowerCase();
    if (countryName) values.add(countryName);
    if (iso3) values.add(iso3);
    return values;
  }, [selectedCountry]);
  const vaccineProgrammesTaRequests = useMemo(() => {
    const teamRows = taRows.filter((row) => isVaccineProgrammesTaTeam(getRowText(row, taLeadCoeTeamColumnKey)));
    if (!selectedCountry) return [];

    return teamRows.filter((row) => (
      taRowMatchesSelectedCountry(row, selectedCountryMatchValues, taCountryColumnKey, taIso3ColumnKey)
    ));
  }, [
    taRows,
    taLeadCoeTeamColumnKey,
    selectedCountry,
    selectedCountryMatchValues,
    taCountryColumnKey,
    taIso3ColumnKey
  ]);

  const toggleTaRequestExpanded = (requestKey) => {
    setExpandedTaRequestKeys((current) => ({
      ...current,
      [requestKey]: !current[requestKey]
    }));
  };

  useEffect(() => {
    setExpandedTaRequestKeys({});
  }, [selectedCountryKey]);

  useEffect(() => {
    setShowAllMatrixCountries(false);
  }, [countryFilter]);

  const vaccineKpis = useMemo(() => {
    const gaviCountries = countryProfiles.filter((profile) => profile.isGaviCountry).length;
    const nonGaviCountries = Math.max(countryProfiles.length - gaviCountries, 0);
    const uniqueAntigens = epiScheduleSummary.find((item) => item.key === 'antigens')?.value || 0;
    const totalItems = epiScheduleSummary.find((item) => item.key === 'routine')?.value || 0;
    const gaviItems = epiScheduleSummary.find((item) => item.key === 'gavi')?.value || 0;
    const gaviShare = totalItems > 0 ? Math.round((gaviItems / totalItems) * 100) : 0;

    return [
      {
        key: 'countries',
        icon: GlobeAltIcon,
        value: countryProfiles.length,
        label: 'Countries in scope',
        meta: `${gaviCountries} Gavi / ${nonGaviCountries} Non-Gavi`
      },
      {
        key: 'antigens',
        icon: BeakerIcon,
        value: uniqueAntigens,
        label: 'Unique antigens',
        meta: `${matrixAntigenColumns.length} shown in matrix`
      },
      {
        key: 'routine',
        icon: CalendarIcon,
        value: totalItems,
        label: 'RI schedule items',
        meta: 'Introduced in RI'
      },
      {
        key: 'gavi',
        icon: HeartIcon,
        value: gaviItems,
        label: 'Gavi funded items',
        meta: `${gaviShare}% of RI schedule items`
      }
    ];
  }, [countryProfiles, epiScheduleSummary, matrixAntigenColumns.length]);

  const matrixCountries = showAllMatrixCountries ? filteredCountries : filteredCountries.slice(0, 8);
  const epiScheduleTableRef = useRef(null);
  const selectedAntigens = useMemo(() => selectedCountry?.antigens || [], [selectedCountry]);
  const epiScheduleExportColumns = useMemo(() => {
    const years = Array.from(new Set(
      selectedAntigens.flatMap((antigen) => (antigen.wuenicSeries || []).map((item) => item.year))
    ))
      .filter((year) => year !== null && year !== undefined && year !== '')
      .sort((a, b) => Number(a) - Number(b))
      .slice(-5);

    return [
      { header: 'Antigen', value: (row) => row.name },
      { header: 'Timeline', value: (row) => row.scheduleTimeline || 'N/A' },
      { header: 'Introduced', value: (row) => row.introDate || (row.isRoutine ? 'Introduced' : 'Not in RI') },
      { header: 'Gavi', value: (row) => (row.isGaviFunded ? 'Yes' : 'No') },
      { header: 'Vaccine Efficacy (WHO)', value: (row) => formatPercentRange(row.typicalVeMin, row.typicalVeMax) },
      { header: 'Effective protection', value: (row) => formatEffectivePopulationProtection(row) },
      ...years.map((year) => ({
        header: String(year),
        value: (row) => {
          const yearRecord = (row.wuenicSeries || []).find((item) => String(item.year) === String(year));
          return formatPercent(yearRecord?.coverage);
        },
      })),
    ];
  }, [selectedAntigens]);
  const selectedWuenicYears = Array.from(new Set(
    selectedAntigens.flatMap((antigen) => (antigen.wuenicSeries || []).map((item) => item.year))
  ))
    .filter((year) => year !== null && year !== undefined && year !== '')
    .sort((a, b) => Number(a) - Number(b))
    .slice(-5);
  const selectedRoutineAntigens = selectedAntigens.filter((antigen) => antigen.isRoutine);
  const selectedGaviAntigens = selectedAntigens.filter((antigen) => antigen.isGaviFunded);
  const selectedGapAntigens = selectedAntigens.filter((antigen) => !antigen.isRoutine).slice(0, 3);
  const selectedBreadthIndexValues = selectedAntigens
    .map((antigen) => getLatestCoverageValue(antigen.wuenicSeries))
    .filter((value) => Number.isFinite(value));
  const selectedBreadthIndex = selectedBreadthIndexValues.length
    ? selectedBreadthIndexValues.reduce((sum, value) => sum + value, 0) / selectedBreadthIndexValues.length
    : null;
  const currentAiNarrative = selectedCountry ? aiNarratives[selectedCountry.key] : '';
  const hpvIntroductionByCountry = useMemo(() => buildHpvIntroductionByCountry(), []);

  useEffect(() => {
    if (!selectedCountry || selectedAntigens.length === 0 || aiNarratives[selectedCountry.key]) return;

    let cancelled = false;

    const generateNarrative = async () => {
      setIsNarrativeLoading(true);
      setNarrativeError('');

      try {
        const openAiModel = process.env.REACT_APP_OPENAI_MODEL || 'gpt-4o-mini';
        const countryPayload = {
          country: selectedCountry.countryName,
          iso3: selectedCountry.iso3,
          fundingStatus: selectedCountry.isGaviCountry ? 'Gavi country' : 'Non-Gavi country',
          routineAntigens: selectedRoutineAntigens.length,
          gaviFundedAntigens: selectedGaviAntigens.length,
          notInRiAntigens: selectedAntigens.filter((antigen) => !antigen.isRoutine).map((antigen) => antigen.name),
          wuenicCoverage: selectedAntigens.map((antigen) => ({
            antigen: antigen.name,
            introducedStatus: antigen.isRoutine ? 'Introduced' : 'Not in RI',
            gaviFunded: antigen.isGaviFunded,
              protectionBreadth: antigen.protectionBreadth || 'N/A',
              typicalVE: formatPercentRange(antigen.typicalVeMin, antigen.typicalVeMax),
              effectivePopulationProtection: formatEffectivePopulationProtection(antigen),
            lastFiveYears: (antigen.wuenicSeries || []).map((item) => ({
              year: item.year,
              coverage: item.coverage
            }))
          }))
        };

        const prompt = `
Write one concise paragraph from a UNICEF immunization programme perspective describing the main gaps and opportunities for this country.
Use only the country data below. Mention important low or declining WUENIC coverage signals, antigens not introduced in RI, and practical opportunities for programme support.
Do not use bullet points. Do not include a title. Keep it under 90 words.

Country data:
${JSON.stringify(countryPayload, null, 2)}
        `.trim();

        const idToken = await authService.getIdToken();
        const response = await fetch(`${OPENAI_PROXY_BASE_URL}/chat/completions`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`
          },
          body: JSON.stringify({
            model: openAiModel,
            temperature: 0.4,
            messages: [{ role: 'user', content: prompt }]
          })
        });

        if (!response.ok) {
          throw new Error(`AI narrative request failed (${response.status})`);
        }

        const payload = await response.json();
        const narrative = payload?.choices?.[0]?.message?.content?.trim();

        if (!cancelled) {
          setAiNarratives((current) => ({
            ...current,
            [selectedCountry.key]: narrative || 'No AI narrative was generated for this country.'
          }));
        }
      } catch (error) {
        if (!cancelled) {
          setNarrativeError('AI narrative is unavailable right now.');
        }
      } finally {
        if (!cancelled) setIsNarrativeLoading(false);
      }
    };

    generateNarrative();

    return () => {
      cancelled = true;
    };
  }, [aiNarratives, selectedAntigens, selectedCountry, selectedGaviAntigens.length, selectedRoutineAntigens.length]);

  return (
    <section className="home-section vaccines-page-section vaccines-dashboard">
      <div className="vaccines-dashboard-header">
        <div>
          <h2 className="vaccines-dashboard-title">Vaccine programmes</h2>
          <p className="vaccines-dashboard-subtitle">
            Track routine immunization programmes, antigen availability, funding status and country-level gaps.
          </p>
        </div>
        <div className="vaccines-segmented-control" aria-label="Country funding filter">
          <button
            type="button"
            className={countryFilter === 'gavi' ? 'is-active' : ''}
            onClick={() => setCountryFilter('gavi')}
          >
            Gavi Countries
          </button>
          <button
            type="button"
            className={countryFilter === 'non-gavi' ? 'is-active' : ''}
            onClick={() => setCountryFilter('non-gavi')}
          >
            Non-Gavi Countries
          </button>
        </div>
      </div>

      <div className="vaccines-kpi-row">
        {vaccineKpis.map((item) => {
          const Icon = item.icon;

          return (
            <article key={item.key} className={`vaccines-kpi-card vaccines-kpi-${item.key}`}>
              <span className="vaccines-kpi-icon" aria-hidden="true">
                <Icon />
              </span>
              <div>
                <p className="vaccines-kpi-value">
                  {isEpiScheduleLoading ? '...' : item.value.toLocaleString()}
                </p>
                <p className="vaccines-kpi-label">{item.label}</p>
                <p className="vaccines-kpi-meta">{item.meta}</p>
              </div>
            </article>
          );
        })}
      </div>

      <div className="vaccines-main-grid">
        <div className="vaccines-left-column">
          <div className="vaccines-schedule-card vaccines-matrix-card">
            <div className="vaccines-card-header">
              <div>
                <p className="vaccines-section-label">Antigen x Country Matrix</p>
                <h3 className="vaccines-card-title">Presence of antigen in routine immunization</h3>
              </div>
              <div className="vaccines-matrix-actions">
                <div className="vaccines-matrix-legend" aria-label="Matrix color legend">
                  <span><i className="matrix-status is-hpv-new-introduction" />New introduction</span>
                  <span><i className="matrix-status is-hpv-follow-up" />Delayed MAC / coverage improvement</span>
                  <span><i className="matrix-status is-gavi-funded" />RI & Gavi funded</span>
                  <span><i className="matrix-status is-routine" />RI non-Gavi funded</span>
                  <span><i className="matrix-status is-not-ri" />Not in RI</span>
                  <span><i className="matrix-status is-empty" />N/A</span>
                </div>
              </div>
            </div>
            <div className="vaccines-matrix-wrap">
              <table className="vaccines-matrix-table vaccines-antigen-matrix" aria-label="Antigen country matrix">
                <thead>
                  <tr>
                    <th scope="col">Country</th>
                    {matrixAntigenColumns.map((antigen) => (
                      <th key={antigen} scope="col">{antigen}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {isEpiScheduleLoading && (
                    <tr>
                      <td colSpan={matrixAntigenColumns.length + 1}>Loading EPI schedule...</td>
                    </tr>
                  )}
                  {!isEpiScheduleLoading && matrixCountries.length === 0 && (
                    <tr>
                      <td colSpan={matrixAntigenColumns.length + 1}>No countries found for this view.</td>
                    </tr>
                  )}
                  {!isEpiScheduleLoading && matrixCountries.map((country) => (
                    <tr
                      key={country.key}
                      className={country.key === selectedCountryKey ? 'is-selected' : ''}
                      aria-selected={country.key === selectedCountryKey}
                    >
                      <th scope="row">
                        <button type="button" onClick={() => setSelectedCountryKey(country.key)}>
                          {country.countryName}
                        </button>
                      </th>
                      {matrixAntigenColumns.map((antigen) => {
                        const isHpvColumn = antigen.toUpperCase() === HPV_ANTIGEN_COLUMN;
                        const hpvIntroduction = isHpvColumn
                          ? hpvIntroductionByCountry.get(normalizePlannedCountryName(country.countryName))
                          : null;
                        const match = country.antigens.find((item) => item.name.toUpperCase() === antigen.toUpperCase());
                        const stateClass = hpvIntroduction?.matrixClass || (!match ? 'is-empty' : match.isRoutine && match.isGaviFunded ? 'is-gavi-funded' : match.isRoutine ? 'is-routine' : 'is-not-ri');
                        const title = hpvIntroduction
                          ? `${country.countryName} - ${antigen}: ${hpvIntroduction.label}${hpvIntroduction.quarters.length ? ` (${hpvIntroduction.quarters.join(', ')})` : ''}`
                          : `${country.countryName} - ${antigen}`;
                        return (
                          <td key={`${country.key}-${antigen}`}>
                            <span className={`matrix-status ${stateClass}`} title={title} />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filteredCountries.length > 8 && (
              <div className="vaccines-matrix-footer">
                <p className="vaccines-footnote">
                  Showing {matrixCountries.length} of {filteredCountries.length} countries in this view.
                </p>
                <button
                  type="button"
                  className="vaccines-expand-button"
                  onClick={() => setShowAllMatrixCountries((current) => !current)}
                >
                  {showAllMatrixCountries ? 'Show fewer countries' : `Show all ${filteredCountries.length} countries`}
                </button>
              </div>
            )}
            <p className="vaccines-footnote vaccines-source-footnote">
              Source: WHO and THRIVE360 data
            </p>
          </div>
        </div>

        <aside className="vaccines-detail-panel" aria-label="Country vaccine details">
          <div className="vaccines-country-header">
            <div>
              <p className="vaccines-section-label">Country snapshot</p>
              <h3>{selectedCountry?.countryName || 'No country selected'}</h3>
            </div>
            {selectedCountry && (
              <span className={`country-badge ${selectedCountry.isGaviCountry ? 'is-gavi' : 'is-non-gavi'}`}>
                {selectedCountry.isGaviCountry ? 'Gavi country' : 'Non-Gavi country'}
              </span>
            )}
          </div>

          <div className="vaccines-snapshot-grid">
            <div><strong>{selectedRoutineAntigens.length}</strong><span>Antigens in RI</span></div>
            <div><strong>{selectedGaviAntigens.length}</strong><span>Gavi funded</span></div>
            <div><strong>{formatPercent(selectedBreadthIndex)}</strong><span>Breadth of protection index</span></div>
            <div><strong>{selectedCountry?.notRoutineCount || 0}</strong><span>Not in RI</span></div>
          </div>

          <div className="vaccines-panel-section">
            <div className="table-export-heading">
              <h4>EPI schedule (As Is)</h4>
              <TableExportMenu
                filename={`epi-schedule-${selectedCountry?.countryName || 'country'}`}
                columns={epiScheduleExportColumns}
                rows={selectedAntigens}
                targetRef={epiScheduleTableRef}
                disabled={isEpiScheduleLoading}
              />
            </div>
            <div className="vaccines-wuenic-table-wrap">
              <table ref={epiScheduleTableRef} className="vaccines-wuenic-table" aria-label="EPI schedule with WUENIC coverage">
                <thead>
                  <tr>
                    <th scope="col">Antigen</th>
                    <th scope="col" className="vaccines-timeline-column">Timeline</th>
                    <th scope="col">Introduced</th>
                    <th scope="col">Gavi</th>
                    <th scope="col">Vaccine Efficacy<br />(WHO)</th>
                    <th scope="col" className="vaccines-epp-column" title="Effective population protection">Effective protection</th>
                    {selectedWuenicYears.map((year) => (
                      <th key={`wuenic-year-${year}`} scope="col">{year}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {selectedAntigens.length === 0 && (
                    <tr>
                      <td colSpan={6 + selectedWuenicYears.length}>No EPI schedule records for this country.</td>
                    </tr>
                  )}
                  {selectedAntigens.map((antigen) => (
                    <tr key={`${selectedCountry?.key}-${antigen.name}-${antigen.scheduleTimeline || 'no-schedule'}-${antigen.wuenicVaccine || 'no-wuenic'}`}>
                      <td>{antigen.name}</td>
                      <td className="vaccines-timeline-column">{antigen.scheduleTimeline || 'N/A'}</td>
                      <td>{antigen.introDate || (antigen.isRoutine ? 'Introduced' : 'Not in RI')}</td>
                      <td>{antigen.isGaviFunded ? 'Yes' : 'No'}</td>
                      <td>
                        {antigen.whoUrl ? (
                          <a href={antigen.whoUrl} target="_blank" rel="noreferrer" className="vaccines-source-link">
                            {formatPercentRange(antigen.typicalVeMin, antigen.typicalVeMax)}
                          </a>
                        ) : (
                          formatPercentRange(antigen.typicalVeMin, antigen.typicalVeMax)
                        )}
                      </td>
                      <td className="vaccines-epp-column">{formatEffectivePopulationProtection(antigen)}</td>
                      {selectedWuenicYears.map((year) => {
                        const yearRecord = (antigen.wuenicSeries || []).find((item) => String(item.year) === String(year));
                        return (
                          <td key={`${selectedCountry?.key}-${antigen.name}-${antigen.scheduleTimeline || 'no-schedule'}-${year}`}>
                            {formatPercent(yearRecord?.coverage)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="vaccines-table-footnote">
              Vaccine Efficacy links to WHO position papers. Effective protection is calculated as latest WUENIC coverage × vaccine efficacy.
            </p>
          </div>

          <div className="vaccines-panel-section">
            <h4>Country Immunization Documents</h4>
            {isImmunizationDocsLoading && (
              <p className="vaccines-empty-note">Loading country immunization documents...</p>
            )}
            {!isImmunizationDocsLoading && immunizationDocsError && (
              <p className="vaccines-empty-note">{immunizationDocsError}</p>
            )}
            {!isImmunizationDocsLoading && !immunizationDocsError && !selectedCountry && (
              <p className="vaccines-empty-note">Select a country to view immunization documents.</p>
            )}
            {!isImmunizationDocsLoading && !immunizationDocsError && selectedCountry && selectedCountryImmunizationDocuments.length === 0 && (
              <p className="vaccines-empty-note">No immunization documents found for {selectedCountry.countryName}.</p>
            )}
            {!isImmunizationDocsLoading && !immunizationDocsError && selectedCountryImmunizationDocuments.length > 0 && (
              <ul className="vaccines-documents-list" aria-label={`Immunization documents for ${selectedCountry?.countryName || 'selected country'}`}>
                {selectedCountryImmunizationDocuments.map((row, index) => {
                  const documentName = getRowText(row, immunizationDocNameColumnKey) || 'Untitled document';
                  const documentUrl = buildDocumentUrl(row, immunizationDocNameColumnKey, immunizationDocFolderColumnKey);
                  const modifiedDate = formatDocumentDate(getRowText(row, immunizationDocModifiedColumnKey));
                  const documentKey = `${selectedCountry?.iso3 || 'country'}-${documentName}-${index}`;

                  return (
                    <li key={documentKey}>
                      {documentUrl ? (
                        <a href={documentUrl} target="_blank" rel="noreferrer" className="vaccines-document-link">
                          {documentName}
                        </a>
                      ) : (
                        <span>{documentName}</span>
                      )}
                      <span className="vaccines-document-meta">{modifiedDate}</span>
                    </li>
                  );
                })}
              </ul>
            )}
            <p className="vaccines-table-footnote">
              Documents from the Immunization Country Intelligence Repository you can find <a href="https://unicef.sharepoint.com/teams/PD-Immunization/SitePages/Immunization-Country-Intelligence-Repository.aspx?csf=1&web=1&e=zHp24n&isSPOFile=1&ovuser=77410195-14e1-4fb8-904b-ab1892023667%2cpkrosniak%40unicef.org&TeamsCID=f9a82d95-c437-405b-8f26-1097a9388c1a&OR=Teams-HL&CT=1780383213103&clickparams=eyJBcHBOYW1lIjoiVGVhbXMtRGVza3RvcCIsIkFwcFZlcnNpb24iOiI1MC8yNjA0MzAxOTIxNiJ9&linkOpenTime=1780383213106&CID=8b9419a2-f0f4-1001-0862-b9a015e6ec3a&cidOR=SPO." target="_blank" rel="noreferrer">here</a>.
            </p>
          </div>

          <div className="vaccines-panel-section">
            <h4>Gaps & Opportunities</h4>
            {isNarrativeLoading && !currentAiNarrative && (
              <p className="vaccines-empty-note">Generating UNICEF perspective...</p>
            )}
            {!isNarrativeLoading && narrativeError && !currentAiNarrative && (
              <p className="vaccines-empty-note">{narrativeError}</p>
            )}
            {currentAiNarrative && (
              <p className="vaccines-ai-narrative">{currentAiNarrative}</p>
            )}
            {selectedGapAntigens.length > 0 && (
              <div className="vaccines-ai-gap-list" aria-label="Data signals used for the AI narrative">
                {selectedGapAntigens.map((antigen) => (
                  <span key={`${selectedCountry?.key}-gap-${antigen.name}`}>{antigen.name} not in RI</span>
                ))}
              </div>
            )}
            <p className="vaccines-ai-footnote">
              AI-generated content based on the selected country EPI schedule and WUENIC data. Review before using in programme decisions.
            </p>
          </div>

        </aside>
      </div>

      <div className="vaccines-schedule-card budgets-ta-card">
        <div className="vaccines-card-header">
          <div>
            <p className="vaccines-section-label">Targeted TA</p>
            <h3 className="vaccines-card-title">Immunization - NVIDR TA Hub requests</h3>
          </div>
          <p className="budgets-data-note">
            {isTaLoading
              ? 'Loading TA Hub requests...'
              : !selectedCountry
                ? 'Select a country in the matrix above'
                : `${vaccineProgrammesTaRequests.length} request${vaccineProgrammesTaRequests.length === 1 ? '' : 's'} for ${selectedCountry.countryName}`}
          </p>
        </div>

        {taError && <p className="budgets-error-note">{taError}</p>}
        {!isTaLoading && !taError && !selectedCountry && (
          <p className="budgets-data-note">Click a country in the matrix above to view Immunization - NVIDR TA requests for that country.</p>
        )}

        <div className="vaccines-wuenic-table-wrap budgets-table-wrap">
          <table className="vaccines-wuenic-table budgets-table budgets-ta-table" aria-label="Immunization - NVIDR TA Hub requests">
            <thead>
              <tr>
                <th scope="col">Country</th>
                <th scope="col">Description</th>
                <th scope="col">Status</th>
                <th scope="col">TA Lead Staff</th>
                <th scope="col">Lead CoE Team</th>
              </tr>
            </thead>
            <tbody>
              {isTaLoading && (
                <tr>
                  <td colSpan="5">Loading Immunization - NVIDR TA requests...</td>
                </tr>
              )}
              {!isTaLoading && !taError && selectedCountry && vaccineProgrammesTaRequests.length === 0 && (
                <tr>
                  <td colSpan="5">No Immunization - NVIDR TA requests found for {selectedCountry.countryName}.</td>
                </tr>
              )}
              {!isTaLoading && vaccineProgrammesTaRequests.map((row, index) => {
                const requestKey = getTaRequestKey(row, index, taTitleColumnKey);
                const description = getRowText(row, taDescriptionColumnKey) || getRowText(row, taTitleColumnKey);
                const isExpanded = Boolean(expandedTaRequestKeys[requestKey]);
                const shouldTruncate = description.length > 200;
                const visibleDescription = shouldTruncate && !isExpanded
                  ? `${description.slice(0, 200).trimEnd()}...`
                  : description;

                return (
                  <tr key={requestKey}>
                    <td>{getRowText(row, taCountryColumnKey) || 'N/A'}</td>
                    <td>
                      {visibleDescription || 'N/A'}
                      {shouldTruncate && (
                        <button
                          type="button"
                          className="budgets-ta-read-more-button"
                          onClick={() => toggleTaRequestExpanded(requestKey)}
                        >
                          {isExpanded ? 'Show less' : 'Read more'}
                        </button>
                      )}
                    </td>
                    <td>{getRowText(row, taStatusColumnKey) || 'N/A'}</td>
                    <td>{getRowText(row, taLeadStaffColumnKey) || 'N/A'}</td>
                    <td>
                      <span className="budgets-ta-priority">{getRowText(row, taLeadCoeTeamColumnKey) || VACCINE_PROGRAMMES_TA_TEAM_NAME}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

const NewVaccineIntro = ({ userClaims, isEmbedded = false }) => {
  if (isEmbedded) {
    return <NewVaccineIntroContent />;
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <TopBar currentPage="new-vaccine-intro" userClaims={userClaims} />
      <div className="v2-module">
        <div className="p-4">
          <NewVaccineIntroContent />
        </div>
      </div>
    </div>
  );
};

export default NewVaccineIntro; 